import React from 'react';
import { TripMatchStatus } from '@marageti/z4-sdk/lib/travel';
import AllTripsView from './all-view';
import useTripMatchSearch from '../../../hooks/use-trip-match-search';
import useURLSearchQuery from '../../../hooks/use-url-for-search-query';
import { sortByValuesAllTrips } from '../options';

interface DefaultSearch {
  [key: string]: string | string[];
  countries: string[];
  departureDate: string;
  departureDateFrom: string;
  departureDateTo: string;
  fullText: string;
  matchDateFrom: string;
  matchDateTo: string;
  order: string;
  partner: string;
  salesStage: string[];
  sort: string;
  status: string[];
}

const defaultSearch: DefaultSearch = {
  countries: [],
  departureDate: '',
  departureDateFrom: '',
  departureDateTo: '',
  fullText: '',
  matchDateFrom: '',
  matchDateTo: '',
  order: sortByValuesAllTrips.createdAt.order,
  partner: 'all',
  salesStage: [],
  sort: sortByValuesAllTrips.createdAt.field,
  status: [],
};

const AllTripsContainer = () => {
  const [queryValues, updateURLQuery] = useURLSearchQuery<DefaultSearch>(defaultSearch);
  const {
    countries,
    departureDateFrom,
    departureDateTo,
    fullText,
    matchDateFrom,
    matchDateTo,
    partner,
    salesStage,
    sort,
    status,
  } = queryValues;

  const sortConfig = sortByValuesAllTrips[sort as keyof typeof sortByValuesAllTrips]
    ?? sortByValuesAllTrips.createdAt;

  const {
    tripMatches,
    error,
    hasMore,
    isLoading,
    loadMore,
    totalHits,
  } = useTripMatchSearch({
    ...queryValues,
    sort: sortConfig.field,
    order: sortConfig.order,
  });

  // The sales stage filter is only enable when active trip matches are included in the search
  const isSalesStageChipFiltersDisabled = status.length > 0 && !status.includes(TripMatchStatus.MatchActive);

  const onChangeFilter = (name: string, v: string | string[]) => {
    switch (name) {
      case 'partner':
        updateURLQuery({ partner: v });
        break;
      case 'sort':
        updateURLQuery({
          sort: v,
          order: sortByValuesAllTrips[v as keyof typeof sortByValuesAllTrips].order,
        });
        break;
      case 'countries':
        updateURLQuery({ countries: v });
        break;
      case 'fullText':
        updateURLQuery({ fullText: v });
        break;
      case 'status': {
        const clearSaleStage = v.length > 0 && !v.includes(TripMatchStatus.MatchActive);
        updateURLQuery({
          status: v,
          salesStage: clearSaleStage ? [] : salesStage,
        });
        break;
      }
      case 'salesStage':
        updateURLQuery({ salesStage: v });
        break;
      case 'matchDateFrom':
      case 'matchDateTo':
      case 'departureDateFrom':
      case 'departureDateTo':
        updateURLQuery({ [name]: typeof v === 'string' ? v : '' });
        break;
      default:
        break;
    }
  };

  const formattedTMCount = new Intl.NumberFormat('en-US').format(totalHits);

  return (
    <AllTripsView
      countries={countries}
      departureDateFrom={departureDateFrom}
      departureDateTo={departureDateTo}
      disableSalesStageChips={isSalesStageChipFiltersDisabled}
      disableSortBy={fullText.length > 0}
      error={error}
      hasMoreItems={hasMore}
      isLoading={isLoading}
      items={tripMatches}
      loadMore={loadMore}
      matchDateFrom={matchDateFrom}
      matchDateTo={matchDateTo}
      onChangeFilter={onChangeFilter}
      partner={partner}
      searchValue={fullText}
      selectedSalesStageChips={salesStage}
      selectedStatusChips={status}
      sortBy={sort}
      totalHits={formattedTMCount}
    />
  );
};

export default AllTripsContainer;
